import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Plus, Edit2, Trash2, Lock, LockOpen } from 'lucide-react';
import { notesService, Note, NoteCreateRequest, NoteUpdateRequest } from '../../../services/notesService';
import { useUser } from '../../../context/UserContext';

interface NotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'customer' | 'invoice';
  entityId: string;
  entityName: string;
  organizationId: string;
  onNoteCountChange?: (delta: number) => void;
}

const NotesModal: React.FC<NotesModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityName,
  organizationId,
  onNoteCountChange
}) => {
  const { accessToken, setUserAndToken } = useUser();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newNote, setNewNote] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editPrivate, setEditPrivate] = useState(false);

  // Load notes when modal opens
  useEffect(() => {
    if (isOpen) {
      loadNotes();
    }
  }, [isOpen, entityId]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await notesService.getNotesByEntity(
        entityType,
        entityId,
        organizationId,
        true, // Include private notes
        accessToken,
        setUserAndToken
      );
      setNotes(response.notes);
    } catch (err: any) {
      setError(err.message || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async () => {
    if (!newNote.trim()) return;

    try {
      const noteData: NoteCreateRequest = {
        entity_type: entityType,
        entity_id: entityId,
        content: newNote.trim(),
        is_private: isPrivate
      };

      const createdNote = await notesService.createNote(
        noteData,
        organizationId,
        accessToken,
        setUserAndToken
      );

      setNotes(prev => [createdNote, ...prev]);
      if (onNoteCountChange) onNoteCountChange(1);
      setNewNote('');
      setIsPrivate(false);
    } catch (err: any) {
      setError(err.message || 'Failed to create note');
    }
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!editContent.trim()) return;

    try {
      const updateData: NoteUpdateRequest = {
        content: editContent.trim(),
        is_private: editPrivate
      };

      const updatedNote = await notesService.updateNote(
        noteId,
        updateData,
        accessToken,
        setUserAndToken
      );

      setNotes(prev => prev.map(note => 
        note.id === noteId ? updatedNote : note
      ));
      setEditingNote(null);
      setEditContent('');
      setEditPrivate(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await notesService.deleteNote(noteId, organizationId, accessToken, setUserAndToken);
      setNotes(prev => prev.filter(note => note.id !== noteId));
      if (onNoteCountChange) onNoteCountChange(-1);
    } catch (err: any) {
      setError(err.message || 'Failed to delete note');
    }
  };

  const startEditing = (note: Note) => {
    setEditingNote(note.id);
    setEditContent(note.content);
    setEditPrivate(note.is_private);
  };

  const cancelEditing = () => {
    setEditingNote(null);
    setEditContent('');
    setEditPrivate(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black bg-opacity-50" 
        onClick={onClose}
      ></div>
      
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <MessageSquare className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Notes for {entityName}
              </h3>
              <p className="text-sm text-gray-500 capitalize">
                {entityType} • {notes.length} note{notes.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-[calc(90vh-120px)]">
          {/* New Note Input */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="space-y-3">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <label className="flex items-center space-x-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={isPrivate}
                      onChange={(e) => setIsPrivate(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Lock className="w-4 h-4" />
                    <span>Private note</span>
                  </label>
                </div>
                <button
                  onClick={handleCreateNote}
                  disabled={!newNote.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Note</span>
                </button>
              </div>
            </div>
          </div>

          {/* Notes List */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading notes...</span>
              </div>
            ) : error ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">No notes yet</h4>
                <p className="text-gray-500">Add your first note above to get started.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                  >
                    {editingNote === note.id ? (
                      // Edit Mode
                      <div className="space-y-3">
                        <textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          rows={3}
                        />
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <label className="flex items-center space-x-2 text-sm text-gray-600">
                              <input
                                type="checkbox"
                                checked={editPrivate}
                                onChange={(e) => setEditPrivate(e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <Lock className="w-4 h-4" />
                              <span>Private note</span>
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleUpdateNote(note.id)}
                              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {note.is_private ? (
                              <Lock className="w-4 h-4 text-amber-600" />
                            ) : (
                              <LockOpen className="w-4 h-4 text-green-600" />
                            )}
                            <span className="text-sm font-medium text-gray-900">
                              {note.user_name || note.administrator_name || 'Unknown'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDate(note.created_at)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => startEditing(note)}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Edit note"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteNote(note.id)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title="Delete note"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="text-gray-700 whitespace-pre-wrap">
                          {note.content}
                        </div>
                        {note.updated_at !== note.created_at && (
                          <div className="text-xs text-gray-500 mt-2">
                            Edited {formatDate(note.updated_at)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotesModal;
